import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { CrashRecoveryService } from './crash-recovery.service';
import { IdleMonitorService } from './idle-monitor.service';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';

/** Which kind of gap triggered this event. Drives which modal renders. */
export type TimerGapKind = 'idle' | 'crash';

/** Unified event shape consumed by the idle and crash modals. Different
 *  detection paths produce the same conceptual data: when did the gap
 *  start and how long was it. */
export interface TimerGapEvent {
    kind: TimerGapKind;
    /** Moment the gap began (epoch ms). For idle this is when the user
     *  stopped interacting; for crash this is the last heartbeat. */
    startedAt: number;
    /** Duration in ms. */
    durationMs: number;
}

/**
 * Single source of truth for "your timer was running and you weren't
 * actually working" prompts.
 *
 * The app has two independent detection paths — `IdleMonitorService`
 * (user was AFK while the app was alive) and `CrashRecoveryService`
 * (the app itself wasn't running) — and they can theoretically both
 * fire close together. Showing two modals back-to-back with the same
 * three options is poor UX, so this service queues events and shows
 * them strictly serially.
 *
 * Lifecycle of an event:
 *
 *   1. One of the detection services emits.
 *   2. If no modal is currently visible AND the event is still
 *      relevant (timer is running, gap is after its current start),
 *      the event is promoted to `activeGap$` and the matching modal
 *      opens.
 *   3. If a modal is already visible, the new event goes on the queue.
 *   4. When the visible modal resolves, the next queued event is
 *      checked for relevance — if the user's action made it irrelevant
 *      (e.g. they stopped the timer), it's silently dropped; otherwise
 *      it's promoted and the next modal opens.
 *
 * Re-evaluating relevance between queue and display matters because
 * the user's choice on the first prompt often resolves the second
 * implicitly — e.g. "Stop timer at last activity" on the crash prompt
 * makes a pending idle prompt moot.
 */
@Injectable({ providedIn: 'root' })
export class TimerGapCoordinatorService implements OnDestroy {
    private readonly _activeGap$ = new BehaviorSubject<TimerGapEvent | null>(null);
    /** Modals subscribe here. They filter by `kind` and open/close
     *  themselves based on whether the active event matches their kind. */
    readonly activeGap$: Observable<TimerGapEvent | null> = this._activeGap$.asObservable();

    private readonly queue: TimerGapEvent[] = [];
    private readonly subs: Subscription[] = [];

    constructor(
        idleMonitor: IdleMonitorService,
        crashRecovery: CrashRecoveryService,
        private timerEntry: TimerEntryService,
    ) {
        this.subs.push(
            idleMonitor.idleResolved$.subscribe(event => {
                // IdleResolvedEvent.startedAt is an ISO string from the
                // preload bridge; convert to epoch ms for the unified shape.
                this.handleGap({
                    kind: 'idle',
                    startedAt: new Date(event.startedAt).getTime(),
                    durationMs: event.durationMs,
                });
            }),
        );
        this.subs.push(
            crashRecovery.recoveryDetected$.subscribe(event => {
                this.handleGap({
                    kind: 'crash',
                    startedAt: event.crashAt.getTime(),
                    durationMs: event.gapMs,
                });
            }),
        );
    }

    /** Called by a modal after the user picks an action. Promotes the
     *  next queued event if one is still relevant. */
    resolve(): void {
        this._activeGap$.next(null);
        // Drain the queue, skipping any event that's no longer relevant
        // (e.g. the timer was stopped by the previous action, so the
        // queued prompt has nothing to act on).
        while (this.queue.length > 0) {
            const next = this.queue.shift()!;
            if (this.isRelevant(next)) {
                this._activeGap$.next(next);
                return;
            }
        }
    }

    private handleGap(event: TimerGapEvent): void {
        if (!this.isRelevant(event)) return;
        if (this._activeGap$.value) {
            this.queue.push(event);
        } else {
            this._activeGap$.next(event);
        }
    }

    private isRelevant(event: TimerGapEvent): boolean {
        // No timer → nothing to recover.
        if (!this.timerEntry.isLiveTimerRunning) return false;
        const liveStart = this.timerEntry.liveStartTime;
        if (!liveStart) return false;
        // Gap began before (or at) the current timer's start — the gap is
        // in already-excluded time (e.g. user discarded the crash gap,
        // moving the start forward past where an earlier idle event lay).
        if (event.startedAt <= liveStart.getTime()) return false;
        return true;
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }
}
